import type { Phone } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  ActionSubmit,
  Label,
  Modal,
  ModalBody,
  ModalButtons,
  ModalButton,
  ModalCloseX,
  ModalContentForm,
  ModalHeading,
  ValidationMessage,
  ValidationContainer,
  PhoneNumberInput,
  useToasterContext,
} from "@tandemdiabetes/sugarmate-react-components"
import {
  useCallback,
  useMemo,
  useState,
} from "react"
import type {
  ReactNode,
  FormEventHandler,
} from "react"
import {
  isSupportedCountry,
  parsePhoneNumber,
} from "react-phone-number-input"

import { DiscardChangesDialog } from "../DiscardChangesDialog"
import { emptyPhone } from "./emptyPhone"
import styles from "./PhoneNumberModal.module.scss"
import { PhoneNumberModalContext } from "./PhoneNumberModalContext"

import {
  CountryCodeSelect,
  defaultCountryCode,
} from "src/components/CountryCodeSelect"
import type { CountryCodeValue } from "src/components/CountryCodeSelect"
import {
  usePolyglot,
  useOverlayLoader,
} from "src/contexts"
import {
  useEditableType,
  useAccountCountry,
  usePromotePhoneToPrimary,
  useCreatePhone,
  usePhones,
} from "src/hooks"
import {
  CreatePhoneErrorToast,
  SavePhoneSuccessToast,
} from "src/toasts"
import {
  injectProps,
  getSugarmateError,
} from "src/utils"

const phoneId = "phone-number-id"
const saveButtonId = "save-phone-id"
const closeButtonId = "close-phone-id"
const phoneErrorId = "phone-number-error-id"
const polyglotPrefix = "components.phone_modal."

interface Props {
  children: ReactNode;
  onSuccess?: (phoneId: Phone["id"]) => Promise<void>;
}

export function PhoneNumberModal(props: Props) {
  const {
    children,
    onSuccess,
  } = props

  const polyglot = usePolyglot()
  const { showToast } = useToasterContext()
  const { setIsOverlayLoaderVisible } = useOverlayLoader()

  const { data: phones } = usePhones()
  const promotePhone = usePromotePhoneToPrimary(onSuccess)
  const createPhone = useCreatePhone(onSuccess)

  const [
    isDiscardOpen,
    setIsDiscardOpen,
  ] = useState<boolean>(false)
  const [
    isModalOpen,
    setIsModalOpen,
  ] = useState<boolean>(false)

  const {
    current: currentPhone,
    original: originalPhone,
    hasChanged: phoneHasChanged,
    save: resetPhone,
    update: editPhone,
  } = useEditableType<Phone>(emptyPhone)

  const accountCountry = useAccountCountry()
  const initialCountryCode: CountryCodeValue = useMemo(
    () => {
      if (!accountCountry?.phone_supported) {
        return defaultCountryCode
      }

      const currentValue = accountCountry.country_code

      if (isSupportedCountry(currentValue)) {
        return currentValue
      }

      return defaultCountryCode
    },
    [accountCountry],
  )

  const [
    countryCode,
    setCountryCode,
  ] = useState<CountryCodeValue>(initialCountryCode)


  // force closes the modal
  const closeModal = useCallback(
    () => {
      setIsModalOpen(false)
    },
    [setIsModalOpen],
  )

  // opens the modal and fills it w/ the specified phone
  const openModal = useCallback(
    (phone: Phone = emptyPhone) => {
      resetPhone(phone)
      const parsedPhoneNumber = parsePhoneNumber(phone.phone_number)
      setCountryCode(parsedPhoneNumber?.country ?? initialCountryCode)
      setIsModalOpen(true)
    },
    [
      setIsModalOpen,
      setCountryCode,
      initialCountryCode,
      resetPhone,
    ],
  )

  // show the warning dialog if changes have been made without saving
  const onOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen) {
        if (phoneHasChanged) {
          setIsDiscardOpen(true)
        } else {
          setIsModalOpen(false)
        }
      }
    },
    [
      phoneHasChanged,
      setIsModalOpen,
      setIsDiscardOpen,
    ],
  )

  const isNewPhone = currentPhone.id === emptyPhone.id

  const contextValue = useMemo(
    () => ({
      openPhoneNumberModal: openModal,
    }),
    [openModal],
  )

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (ev) => {
      ev.preventDefault()
      ev.stopPropagation()

      if (currentPhone.phone_number === originalPhone.phone_number) {
        showToast(
          "save-phone-success-toast",
          SavePhoneSuccessToast,
        )

        closeModal()
        return
      }

      void (async () => {
        setIsOverlayLoaderVisible(true)

        const matchingPhone = phones?.find(
          (phone) => phone.phone_number === currentPhone.phone_number && phone.deleted_at === null,
        )

        try {
          if (!promotePhone || !createPhone) {
            throw new Error("cannot create phone")
          }

          await (
            matchingPhone
              ? promotePhone(matchingPhone.id)
              : createPhone({
                phone_number: currentPhone.phone_number,
                is_primary: true,
              })
          )

          closeModal()

          showToast(
            "save-phone-success-toast",
            SavePhoneSuccessToast,
          )
        } catch (e) {
          const sugarmateError = getSugarmateError(e)

          const ErrorToast = injectProps(
            CreatePhoneErrorToast,
            {
              code: sugarmateError?.code,
              status: sugarmateError?.status,
            },
          )

          showToast(
            "save-phone-error-toast",
            ErrorToast,
          )
        }

        setIsOverlayLoaderVisible(false)
      })()
    },
    [
      showToast,
      promotePhone,
      createPhone,
      currentPhone,
      originalPhone,
      closeModal,
      setIsOverlayLoaderVisible,
      phones,
    ],
  )

  const onPhoneChange = useCallback(
    (value?: string) => {
      editPhone({
        ...currentPhone,
        phone_number: value ?? "",
      })
    },
    [
      editPhone,
      currentPhone,
    ],
  )

  return (
    <Modal
      open={isModalOpen}
      onOpenChange={onOpenChange}
    >
      <PhoneNumberModalContext.Provider value={contextValue}>
        {children}
      </PhoneNumberModalContext.Provider>
      <ModalContentForm onSubmit={onSubmit}>
        <ModalHeading>
          {polyglot.t(`${polyglotPrefix}${isNewPhone ? "add_title" : "edit_title"}`)}
          <ModalCloseX />
        </ModalHeading>
        <ModalBody className={styles.modalBody}>
          <div>
            <Label className={styles.label}>
              {polyglot.t(`${polyglotPrefix}country_code.label`)}
            </Label>
            <CountryCodeSelect
              countryCode={countryCode}
              setCountryCode={setCountryCode}
              required
            />
          </div>
          <div>
            <Label className={styles.label}>
              {polyglot.t(`${polyglotPrefix}phone_number.label`)}
            </Label>
            <ValidationContainer>
              <PhoneNumberInput
                id={phoneId}
                className={styles.phoneInput}
                country={!countryCode ? undefined : countryCode}
                placeholder={polyglot.t(`${polyglotPrefix}phone_number.placeholder`)}
                value={currentPhone.phone_number}
                onChange={onPhoneChange}
                autoComplete="tel"
                required
              />
              <ValidationMessage
                id={phoneErrorId}
                invalidMessage={{
                  customError: polyglot.t("validation_message.phone_invalid"),
                  valueMissing: polyglot.t("validation_message.value_missing"),
                }}
              />
            </ValidationContainer>
          </div>
          <ModalButtons>
            <ModalButton
              stylePreset="secondary"
              type="button"
              id={closeButtonId}
            >
              {polyglot.t(`${polyglotPrefix}cancel_cta`)}
            </ModalButton>
            <ActionSubmit
              className={styles.modalMultiButton}
              stylePreset="primary"
              value={polyglot.t(`${polyglotPrefix}save_cta`)}
              disabled={!phoneHasChanged}
              id={saveButtonId}
            />
          </ModalButtons>
        </ModalBody>
        <DiscardChangesDialog
          open={isDiscardOpen}
          setOpen={setIsDiscardOpen}
          discardChanges={closeModal}
        />
      </ModalContentForm>
    </Modal>
  )
}
